import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';

import { modifier } from 'ember-modifier';

import {
  BoxelInput,
  type BoxelInputBottomTreatments,
} from '@cardstack/boxel-ui/components';
import { IconSearch } from '@cardstack/boxel-ui/icons';
import { autoFocus } from '@cardstack/boxel-ui/modifiers';

import RealmPicker, {
  type RealmFilter,
} from '@cardstack/host/components/realm-picker';
import TypePicker, {
  type TypeFilter,
} from '@cardstack/host/components/type-picker';

let elementCallback = modifier(
  (element, [callback]: [((element: HTMLElement) => void) | undefined]) => {
    if (callback) {
      callback(element as HTMLElement);
    }
  },
);

interface Signature {
  Element: HTMLElement;
  Args: {
    value: string;
    realmFilter: RealmFilter;
    typeFilter: TypeFilter;
    placeholder?: string;
    autocomplete?: string;
    onInput?: (value: string) => void;
    onFocus?: (ev: Event) => void;
    onBlur?: (ev: Event) => void;
    onKeyDown?: (ev: Event) => void;
    onInputInsertion?: (element: HTMLElement) => void;
    pickerDestination?: string;
    bottomTreatment?: BoxelInputBottomTreatments;
    state?: 'none' | 'valid' | 'invalid' | 'loading' | 'initial';
    id?: string;
  };
  Blocks: {};
}

export default class SearchBar extends Component<Signature> {
  @action
  handleKeyDown(ev: Event): void {
    this.args.onKeyDown?.(ev);
  }

  <template>
    <div
      class='search-sheet__search-bar'
      data-test-search-sheet-search-bar
      ...attributes
    >
      <div class='search-sheet__search-bar-icon' aria-hidden='true'>
        <IconSearch
          class='search-sheet__search-bar-search-icon'
          width='18'
          height='18'
        />
      </div>
      <div class='search-sheet__search-bar-picker'>
        <RealmPicker
          @filter={{@realmFilter}}
          @destination={{@pickerDestination}}
        />
      </div>
      <div class='search-sheet__search-bar-picker'>
        <TypePicker
          @filter={{@typeFilter}}
          @destination={{@pickerDestination}}
        />
      </div>
      <div class='search-sheet__search-bar-separator' aria-hidden='true'></div>
      {{! template-lint-disable no-invalid-interactive }}
      <div
        class='search-sheet__search-bar-input-wrap'
        {{on 'keydown' this.handleKeyDown}}
      >
        <BoxelInput
          class='search-sheet__search-bar-input'
          @type='text'
          @bottomTreatment={{@bottomTreatment}}
          @value={{@value}}
          @state={{@state}}
          @placeholder={{@placeholder}}
          @onFocus={{@onFocus}}
          @onInput={{@onInput}}
          @onBlur={{@onBlur}}
          @autocomplete={{@autocomplete}}
          id={{@id}}
          {{elementCallback @onInputInsertion}}
          {{autoFocus}}
          data-search-field
          data-test-search-field
        />
      </div>
    </div>
    <style scoped>
      .search-sheet__search-bar {
        --search-bar-bg: var(
          --boxel-input-search-background-color,
          var(--foreground, var(--boxel-dark))
        );
        --search-bar-color: var(
          --boxel-input-search-color,
          var(--background, var(--boxel-light))
        );
        --search-bar-border-color: var(--border, var(--boxel-dark));
        --search-bar-separator-color: rgba(255, 255, 255, 0.25);

        display: flex;
        align-items: center;
        gap: var(--boxel-sp-xs);
        flex-wrap: wrap;
        min-height: 3.125rem;
        background-color: var(--search-bar-bg);
        color: var(--search-bar-color);
        border: 1px solid var(--search-bar-border-color);
        border-radius: var(
          --boxel-form-control-border-radius,
          var(--boxel-border-radius-xl)
        );
        outline: 1px solid transparent;
        transition:
          outline-color var(--boxel-transition),
          border-color var(--boxel-transition);
      }

      .search-sheet__search-bar:focus-within {
        outline: 2px solid var(--ring, var(--boxel-highlight));
        border-color: var(--ring, var(--boxel-highlight));
      }

      .search-sheet__search-bar :deep(.boxel-input),
      .search-sheet__search-bar .search-sheet__search-bar-input {
        outline: none !important;
      }
      .search-sheet__search-bar-input {
        padding-left: var(--boxel-sp-2xs);
      }

      .search-sheet__search-bar :deep(.boxel-input:focus),
      .search-sheet__search-bar :deep(.boxel-input:focus-visible) {
        outline: none !important;
        border-color: transparent !important;
      }

      .search-sheet__search-bar:focus-within
        :deep(.ember-power-select-trigger:focus),
      .search-sheet__search-bar:focus-within
        :deep(.ember-power-select-trigger:focus-visible) {
        outline: none;
      }

      .search-sheet__search-bar-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-left: var(--boxel-sp-xs);
        flex-shrink: 0;
      }

      .search-sheet__search-bar-search-icon {
        --icon-color: var(
          --boxel-input-search-icon-color,
          var(--boxel-highlight)
        );
        color: var(--icon-color);
      }

      .search-sheet__search-bar-picker {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        min-width: 0;
      }

      .search-sheet__search-bar-picker :deep(.boxel-trigger) {
        border: none;
        color: var(--boxel-dark);
        padding: var(--boxel-sp-3xs);
      }

      .search-sheet__search-bar-separator {
        width: 1px;
        background-color: var(--search-bar-separator-color);
        flex-shrink: 0;
      }

      .search-sheet__search-bar-input-wrap {
        flex: 1;
        display: flex;
      }

      .search-sheet__search-bar-input-wrap :deep(.boxel-input) {
        border: none;
        border-radius: 0;
        background: transparent;
        color: inherit;
        border-left: none;
      }

      .search-sheet__search-bar-input-wrap :deep(.input-container) {
        border: none;
        border-radius: 0;
        background: transparent;
      }
    </style>
  </template>
}
